import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

type Props = {
  words: string[];
  className?: string;
  /** ms each word stays before flipping. */
  interval?: number;
  /** ms per word the swap animation takes. */
  duration?: number;
};

/**
 * Aceternity container-text-flip: a single word that cycles through
 * a list. The pill container animates to the new width as the next
 * word slides in, so long words don't break the layout.
 */
export function ContainerTextFlip({
  words,
  className,
  interval = 2400,
  duration = 0.55
}: Props) {
  const [i, setI] = React.useState(0);
  const measureRef = React.useRef<HTMLSpanElement>(null);
  const [w, setW] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    if (words.length <= 1) return;
    const t = window.setInterval(() => {
      setI(n => (n + 1) % words.length);
    }, interval);
    return () => window.clearInterval(t);
  }, [interval, words.length]);

  // Re-measure on word change.
  React.useLayoutEffect(() => {
    if (measureRef.current) {
      setW(measureRef.current.offsetWidth);
    }
  }, [i]);

  const current = words[i % words.length];

  return (
    <motion.span
      layout
      transition={{ type: "spring", stiffness: 220, damping: 24 }}
      animate={{ width: w }}
      className={cn(
        "relative inline-flex items-center justify-center overflow-hidden",
        "rounded-2xl bg-gradient-to-br from-accent/20 via-accent/10 to-transparent",
        "border border-accent-lo px-4 py-1",
        "align-baseline",
        className
      )}
      style={{ whiteSpace: "nowrap" }}
    >
      {/* Invisible measuring node — sets the width to match the current word. */}
      <span
        ref={measureRef}
        aria-hidden
        className="invisible absolute top-0 left-0 px-4"
        style={{ whiteSpace: "nowrap" }}
      >
        {current}
      </span>

      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={i}
          initial={{ y: "100%", opacity: 0, filter: "blur(8px)" }}
          animate={{ y: 0,      opacity: 1, filter: "blur(0px)" }}
          exit={{    y: "-100%", opacity: 0, filter: "blur(8px)" }}
          transition={{ duration, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {current}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}
